import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { generateRequestIdV7 } from "../lib/dispatch";
import { useCachedResource } from "../lib/resource-cache";
import { parseEmbeddedSignupMessage } from "../lib/wa-signup";

/**
 * /whatsapp-signup — WhatsApp Embedded Signup (coexistence onboarding).
 *
 * 2026-08-25 (whatsapp-embedded-signup-page). Hosts Meta's Embedded
 * Signup popup so the studio's WhatsApp Business App number can be
 * coexistence-onboarded onto the Cloud API (runbook
 * `build-output/runbooks/whatsapp-coexistence-migration.md` §3-4). Flow:
 *
 *   1. GET /api/whatsapp-signup/:slug → the non-secret popup constants
 *      (Meta app id + Facebook Login for Business config id) + the
 *      pending-signup presence flags.
 *   2. Load Meta's JS SDK (connect.facebook.net — the SPA's one external
 *      script) and FB.init with those constants.
 *   3. Launch → FB.login opens the popup with
 *      `featureType: "whatsapp_business_app_onboarding"` — the flag that
 *      makes the popup offer "connect your existing WhatsApp Business
 *      app number" (without it only the standard new-number flow shows).
 *      The operator signs into the BUSINESS's Facebook and the phone
 *      confirms in-app.
 *   4. Two async results race back: the popup's window message (waba_id,
 *      maybe phone_number_id) and the FB.login callback (the single-use
 *      token code). When BOTH are in hand the page auto-POSTs the
 *      completion — the code is short-lived, so no manual submit step.
 *   5. The backend exchanges + verifies + subscribes, then PARKS the
 *      credentials as pending secret kinds. Production keeps sending
 *      from the current number — the explicit Activate button below is
 *      the repoint (runbook §6), pressed only after the new WABA's
 *      templates are approved. If the WABA lists several numbers the
 *      backend answers with a phone chooser instead (no new popup run
 *      needed — the parked token covers the follow-up).
 */

interface FbSdk {
  init(opts: {
    appId: string;
    autoLogAppEvents: boolean;
    xfbml: boolean;
    version: string;
  }): void;
  login(
    cb: (response: {
      authResponse?: { code?: string } | null;
      status?: string;
    }) => void,
    opts: {
      config_id: string;
      response_type: string;
      override_default_response_type: boolean;
      extras: Record<string, unknown>;
    },
  ): void;
}

declare global {
  interface Window {
    FB?: FbSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_SCRIPT_ID = "facebook-jssdk";
const SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";

interface SignupConstants {
  readonly appId: string;
  readonly configId: string;
  readonly graphVersion: string;
}

interface PendingPresence {
  readonly access_token: boolean;
  readonly phone_number_id: boolean;
  readonly waba_id: boolean;
}

interface PendingResult {
  readonly waba_id: string;
  readonly phone_number_id: string;
  readonly display_phone_number: string | null;
}

interface PhoneChoice {
  readonly wabaId: string;
  readonly rows: ReadonlyArray<{
    readonly id: string;
    readonly display_phone_number: string | null;
  }>;
}

export function WhatsappSignup(): JSX.Element {
  const { data: who } = useCachedResource("whoami", () => api.whoami());
  const slug = who?.studio.slug ?? null;

  const [constants, setConstants] = useState<SignupConstants | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [pending, setPending] = useState<PendingPresence | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [session, setSession] = useState<{
    wabaId: string;
    phoneNumberId: string | null;
    event: string;
  } | null>(null);
  const [cancelStep, setCancelStep] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  // The parked-pending result card persists until its own Dismiss — the
  // flow cannot be re-run to regenerate it (the code is single-use).
  const [result, setResult] = useState<PendingResult | null>(null);
  const [phoneChoice, setPhoneChoice] = useState<PhoneChoice | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [activateArmed, setActivateArmed] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activated, setActivated] = useState<{
    phone_number_id: string;
  } | null>(null);
  const submittedRef = useRef(false);

  // 1 — the popup constants + the pending-signup presence flags.
  // refresh() re-reads after each op so the Activate panel tracks state.
  const [configNonce, setConfigNonce] = useState(0);
  const refreshConfig = useCallback(() => setConfigNonce((n) => n + 1), []);
  useEffect(() => {
    if (slug === null) return;
    let cancelled = false;
    api
      .getWhatsappSignupConfig(slug)
      .then((r) => {
        if (cancelled) return;
        setConfigured(r.configured);
        setPending(r.pending ?? null);
        if (
          r.configured &&
          r.app_id !== undefined &&
          r.config_id !== undefined &&
          r.graph_version !== undefined
        ) {
          setConstants({
            appId: r.app_id,
            configId: r.config_id,
            graphVersion: r.graph_version,
          });
        }
        setLoadError(null);
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, configNonce]);

  // 2 — the SDK (loaded once, only when the studio is configured).
  useEffect(() => {
    if (constants === null) return;
    if (window.FB !== undefined) {
      setSdkReady(true);
      return;
    }
    window.fbAsyncInit = () => {
      window.FB?.init({
        appId: constants.appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: constants.graphVersion,
      });
      setSdkReady(true);
    };
    if (document.getElementById(SDK_SCRIPT_ID) === null) {
      const script = document.createElement("script");
      script.id = SDK_SCRIPT_ID;
      script.src = SDK_SRC;
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      document.body.appendChild(script);
    }
  }, [constants]);

  // 4a — the popup's session-info messages.
  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const parsed = parseEmbeddedSignupMessage(event.origin, event.data);
      if (parsed.kind === "finish") {
        setSession({
          wabaId: parsed.wabaId,
          phoneNumberId: parsed.phoneNumberId,
          event: parsed.event,
        });
        setCancelStep(null);
      } else if (parsed.kind === "cancel") {
        setCancelStep(parsed.currentStep ?? "(unknown step)");
        setPopupOpen(false);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // 4b/5 — auto-complete when both halves are in hand.
  useEffect(() => {
    if (slug === null || code === null || session === null) return;
    if (submittedRef.current) return;
    submittedRef.current = true;
    setCompleting(true);
    setCompleteError(null);
    api
      .completeWhatsappSignup(slug, {
        request_id: generateRequestIdV7(),
        code,
        waba_id: session.wabaId,
        ...(session.phoneNumberId !== null
          ? { phone_number_id: session.phoneNumberId }
          : {}),
      })
      .then((r) => {
        // The code is consumed either way — clear the run's halves so the
        // next launch starts clean (a stale latch here made a post-success
        // relaunch silently never POST).
        submittedRef.current = false;
        setCode(null);
        setSession(null);
        if (r.outcome === "phone_choice_required") {
          setPhoneChoice({
            wabaId: r.waba_id,
            rows: r.phone_numbers ?? [],
          });
        } else {
          setResult({
            waba_id: r.waba_id,
            phone_number_id: r.phone_number_id ?? "(unknown)",
            display_phone_number: r.display_phone_number ?? null,
          });
        }
        refreshConfig();
      })
      .catch((e: Error) => {
        setCompleteError(e.message);
        // Allow a retry with a FRESH popup run — the consumed code is dead.
        submittedRef.current = false;
        setCode(null);
        setSession(null);
      })
      .finally(() => {
        setCompleting(false);
      });
  }, [slug, code, session, refreshConfig]);

  const launch = (): void => {
    if (constants === null || window.FB === undefined) return;
    // A fresh run must never pair with a previous run's half-delivered
    // code or session — clear both plus the submit latch up front.
    submittedRef.current = false;
    setCode(null);
    setSession(null);
    setPopupOpen(true);
    setCancelStep(null);
    setCompleteError(null);
    window.FB.login(
      (response) => {
        setPopupOpen(false);
        const c = response.authResponse?.code;
        if (typeof c === "string" && c.length > 0) {
          setCode(c);
        }
      },
      {
        config_id: constants.configId,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          // The coexistence selector: without this flag the popup offers
          // ONLY the standard new-Cloud-API-number flow and the
          // "connect your existing WhatsApp Business app number" screen
          // never appears (Meta ES coexistence doc).
          featureType: "whatsapp_business_app_onboarding",
          sessionInfoVersion: "3",
        },
      },
    );
  };

  const selectPhone = (phoneNumberId: string): void => {
    if (slug === null || phoneChoice === null) return;
    setSelecting(true);
    setCompleteError(null);
    api
      .selectWhatsappSignupPhone(slug, {
        request_id: generateRequestIdV7(),
        phone_number_id: phoneNumberId,
      })
      .then((r) => {
        setPhoneChoice(null);
        setResult({
          waba_id: r.waba_id,
          phone_number_id: r.phone_number_id,
          display_phone_number: r.display_phone_number,
        });
        refreshConfig();
      })
      .catch((e: Error) => {
        setCompleteError(e.message);
      })
      .finally(() => {
        setSelecting(false);
      });
  };

  const activate = (): void => {
    if (slug === null) return;
    setActivating(true);
    setActivateError(null);
    api
      .activateWhatsappSignup(slug, { request_id: generateRequestIdV7() })
      .then((r) => {
        setActivated({ phone_number_id: r.phone_number_id });
        setActivateArmed(false);
        refreshConfig();
      })
      .catch((e: Error) => {
        setActivateError(e.message);
        setActivateArmed(false);
      })
      .finally(() => {
        setActivating(false);
      });
  };

  const pendingReady =
    pending !== null && pending.access_token && pending.phone_number_id;
  const pendingStranded =
    pending !== null &&
    pending.access_token &&
    !pending.phone_number_id &&
    phoneChoice === null;

  return (
    <div>
      <h1 className="page-title">WhatsApp signup</h1>
      <p className="hint">
        Coexistence onboarding: connect the studio's WhatsApp Business App
        number to the Cloud API so the human keeps the app and the AI keeps
        the API — one number, both senders. The phone running the Business
        App must be in hand for the in-app confirmation step.
      </p>

      {loadError !== null ? (
        <div className="panel">
          <p className="error-text">Failed to load signup config: {loadError}</p>
        </div>
      ) : null}

      {configured === false ? (
        <div className="panel">
          <h2>Not configured</h2>
          <p className="hint">
            This studio has no <code>whatsapp_embedded_signup</code> config
            block (Meta app id + Embedded Signup config id), so the popup
            cannot launch. Seed it in the studio's L2 YAML or via a /config
            Save, then reload.
          </p>
        </div>
      ) : null}

      {activated !== null ? (
        <div className="panel">
          <h2>Production repointed ✓</h2>
          <p>
            Live WhatsApp sends now use phone number id{" "}
            <code>{activated.phone_number_id}</code>. The pending credentials
            were promoted and cleared.
          </p>
          <p className="hint">
            Next (runbook §7-9): run the live echo smoke walk, then the
            public wa.me sweep. Keep the Business App opened at least every
            13 days.
          </p>
          <button type="button" onClick={() => setActivated(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      {result !== null ? (
        <div className="panel">
          <h2>Number onboarded — parked as pending ✓</h2>
          <p>
            WhatsApp number{" "}
            <strong>{result.display_phone_number ?? "(unknown display)"}</strong>{" "}
            is connected to the Cloud API and its credentials are stored as
            PENDING. Production still sends from the current number.
          </p>
          <ul>
            <li>
              WABA id: <code>{result.waba_id}</code>
            </li>
            <li>
              Phone number id: <code>{result.phone_number_id}</code>
            </li>
            <li>Business token parked + webhooks subscribed.</li>
          </ul>
          <p className="hint">
            Next steps (runbook §7): resubmit the message templates on the
            new WABA and wait for member_verification_code and
            layer_notification to be APPROVED — then press Activate below to
            repoint production. Keep the Business App opened at least every
            13 days.
          </p>
          <button type="button" onClick={() => setResult(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      {phoneChoice !== null ? (
        <div className="panel">
          <h2>Choose the phone number</h2>
          <p className="hint">
            WABA <code>{phoneChoice.wabaId}</code> lists more than one phone
            number and the popup did not say which one was onboarded. The
            token is already parked — pick the number this signup was for
            (no new popup run needed).
          </p>
          <ul>
            {phoneChoice.rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  disabled={selecting}
                  onClick={() => selectPhone(row.id)}
                >
                  {row.display_phone_number ?? "(unknown display)"} —{" "}
                  {row.id}
                </button>
              </li>
            ))}
          </ul>
          {selecting ? <p className="hint">Verifying the choice…</p> : null}
          {completeError !== null ? (
            <p className="error-text">Phone selection failed: {completeError}</p>
          ) : null}
        </div>
      ) : null}

      {pendingStranded && result === null ? (
        <div className="panel">
          <h2>Parked signup without a phone choice</h2>
          <p className="hint">
            A pending token is parked but no phone number was selected
            (likely a reload mid-choice). Launch the signup again — the
            fresh run replaces the parked token and re-offers the chooser.
          </p>
        </div>
      ) : null}

      {pendingReady ? (
        <div className="panel">
          <h2>Pending activation</h2>
          <p className="hint">
            A parked signup is ready. Activating REPOINTS ALL production
            WhatsApp sends (AI replies, login codes, staff alerts) to the
            new number immediately — only press it once the new WABA's
            templates (member_verification_code + layer_notification) are
            APPROVED, per runbook §6-7.
          </p>
          {activateArmed ? (
            <p className="error-text">
              Confirm: production repoints on the next send. This is the
              runbook §6 config swap.
            </p>
          ) : null}
          <button
            type="button"
            disabled={activating}
            onClick={() => {
              if (activateArmed) {
                activate();
              } else {
                setActivateArmed(true);
              }
            }}
          >
            {activating
              ? "Activating…"
              : activateArmed
                ? "Confirm — repoint production now"
                : "Activate new number…"}
          </button>
          {activateArmed && !activating ? (
            <button type="button" onClick={() => setActivateArmed(false)}>
              Cancel
            </button>
          ) : null}
          {activateError !== null ? (
            <p className="error-text">Activation failed: {activateError}</p>
          ) : null}
        </div>
      ) : null}

      {configured === true && result === null && phoneChoice === null ? (
        <div className="panel">
          <h2>Run the signup</h2>
          <p className="hint">
            The popup asks for the business's Facebook login, then offers
            "connect your existing WhatsApp Business app number". Decline
            chat-history sharing when the app asks (v1 decision — the echo
            lane only mirrors NEW messages).
          </p>
          <button
            type="button"
            disabled={!sdkReady || popupOpen || completing}
            onClick={launch}
          >
            {popupOpen
              ? "Popup open…"
              : completing
                ? "Completing…"
                : sdkReady
                  ? "Launch WhatsApp signup"
                  : "Loading Meta SDK…"}
          </button>
          {session !== null && code === null ? (
            <p className="hint">
              Signup finished (WABA {session.wabaId}) — waiting for the
              login callback's token code…
            </p>
          ) : null}
          {code !== null && session === null ? (
            <p className="hint">
              Token code received — waiting for the popup's session info…
            </p>
          ) : null}
          {completing ? (
            <p className="hint">
              Exchanging the code, verifying the number and subscribing
              webhooks…
            </p>
          ) : null}
          {cancelStep !== null ? (
            <p className="hint">
              Signup was abandoned at step: <code>{cancelStep}</code>. Launch
              again to retry.
            </p>
          ) : null}
          {completeError !== null ? (
            <p className="error-text">
              Completion failed: {completeError}. The popup code is single-use
              — launch the signup again to retry.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
